import React, { useContext } from "react";
import { CommitteeContext } from "../AppContext/CommitteeContext";
import { Tooltip } from "@mui/material";
import { getAreaForAction, getDefaultType } from "./permissionMap";

/**
 * AreaGate — wraps action elements with type-based access control.
 *
 * Button types (stored in Firestore per community, defaults from permissionMap):
 *   PUBLIC     → anyone in the community can use it
 *   RESTRICTED → only committee members with the relevant area
 *   HIDDEN     → invisible to unauthorized users
 *
 * PREFERRED USAGE (registry-backed):
 *   <GatedAction actionId={ACTION_IDS.FINANCE_ACCOUNT_CREATE}>
 *     <Button onClick={handleCreate}>Add Account</Button>
 *   </GatedAction>
 *
 *   const { allowed } = useAction(ACTION_IDS.FINANCE_ACCOUNT_CREATE);
 *
 * LEGACY USAGE (still works):
 *   <AreaGate area="finance">...</AreaGate>
 *   const { allowed } = useAreaGate("finance");
 */

/**
 * Hook (by area) — returns { allowed, ownerName, ownerRole }
 * allowed = true when user has the given area OR no committee is set up yet
 */
export function useAreaGate(area) {
  const { hasArea, committeeMembers, loading } = useContext(CommitteeContext);

  // While loading, allow (don't flash disabled state)
  if (loading) return { allowed: true, ownerName: null, ownerRole: null };

  // If no committee is configured at all, allow everyone (bootstrap mode)
  if (!committeeMembers || committeeMembers.length === 0) {
    return { allowed: true, ownerName: null, ownerRole: null };
  }

  // Find who owns this area (for tooltip messaging)
  const owner = committeeMembers.find(
    (m) => m.areas && m.areas.includes(area)
  );

  if (hasArea(area)) {
    return { allowed: true, ownerName: null, ownerRole: null };
  }

  return {
    allowed: false,
    ownerName: owner?.displayName || null,
    ownerRole: owner?.role?.replace(/[-_]/g, " ") || null,
  };
}

/**
 * Hook (by actionId) — checks button type then applies access rules:
 *   1. Resolve effective type: Firestore override → defaultType from permissionMap
 *   2. PUBLIC  → allowed for everyone
 *   3. RESTRICTED → only committee members with the relevant area
 *   4. HIDDEN → only committee members with the area, otherwise completely hidden
 */
export function useAction(actionId) {
  const { getActionType, myRoles, committeeMembers, loading, isFeatureActionEnabled, hasArea } =
    useContext(CommitteeContext);
  const area = getAreaForAction(actionId);

  // While loading, allow (don't flash disabled state)
  if (loading) return { allowed: true, hide: false, ownerName: null, ownerRole: null, actionType: "public" };

  // If no committee configured, allow everyone (bootstrap mode)
  if (!committeeMembers || committeeMembers.length === 0) {
    return { allowed: true, hide: false, ownerName: null, ownerRole: null, actionType: "public" };
  }

  // Check community-level toggle first (Firestore enabled/disabled)
  if (!isFeatureActionEnabled(actionId)) {
    return {
      allowed: false,
      hide: false,
      ownerName: null,
      ownerRole: null,
      actionType: "disabled",
      disabledReason: "This action has been disabled by your community admin",
    };
  }

  // Resolve effective type: Firestore override → default from permissionMap
  const effectiveType = getActionType ? getActionType(actionId) : getDefaultType(actionId);

  // PUBLIC → everyone allowed
  if (effectiveType === "public") {
    return { allowed: true, hide: false, ownerName: null, ownerRole: null, actionType: "public" };
  }

  // RESTRICTED or HIDDEN → check area ownership
  const areaAllowed = area ? hasArea(area) : false;

  // Find area owner for tooltip
  const owner = committeeMembers.find(
    (m) => m.areas && m.areas.includes(area)
  );

  if (areaAllowed) {
    return { allowed: true, hide: false, ownerName: null, ownerRole: null, actionType: effectiveType };
  }

  // Not allowed — hidden type hides entirely, restricted shows disabled
  return {
    allowed: false,
    hide: effectiveType === "hidden",
    ownerName: owner?.displayName || null,
    ownerRole: owner?.role?.replace(/[-_]/g, " ") || null,
    actionType: effectiveType,
  };
}

/* ── Render helper (shared by AreaGate and GatedAction) ──── */
function renderGated({ allowed, ownerName, ownerRole, disabledReason, children, fallback, hide, actionId }) {
  // Stamp data-aid on children so the dev tool can detect registered buttons
  const stampChildren = (nodes) =>
    React.Children.map(nodes, (child) => {
      if (!React.isValidElement(child)) return child;
      return React.cloneElement(child, actionId ? { "data-aid": actionId } : {});
    });

  if (allowed) return <>{stampChildren(children)}</>;

  if (hide) return null;

  if (fallback) return <>{fallback}</>;

  const tooltipMsg = disabledReason
    ? disabledReason
    : ownerName
      ? `Only ${ownerRole} (${ownerName}) can do this`
      : "Restricted to committee members with this area";

  return (
    <Tooltip title={tooltipMsg} arrow>
      <span style={{ display: "inline-block" }}>
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return child;
          return React.cloneElement(child, {
            ...(actionId ? { "data-aid": actionId } : {}),
            disabled: true,
            onClick: undefined,
            sx: {
              ...child.props.sx,
              opacity: 0.5,
              pointerEvents: "none",
            },
          });
        })}
      </span>
    </Tooltip>
  );
}

/**
 * GatedAction — PREFERRED component. Uses actionId from permissionMap.
 *
 * Props:
 *   actionId  — constant from actionIds.js (e.g. ACTION_IDS.FINANCE_ACCOUNT_CREATE)
 *   children  — the action element(s) to gate
 *   fallback  — optional custom fallback
 *   hide      — if true, forces hide (auto-set for HIDDEN type)
 */
export function GatedAction({ actionId, children, fallback, hide: hideProp = false }) {
  const gate = useAction(actionId);
  const shouldHide = hideProp || gate.hide;
  return renderGated({ ...gate, children, fallback, hide: shouldHide, actionId });
}

/**
 * AreaGate — LEGACY component. Uses area directly.
 * Kept for backward compatibility.
 */
export default function AreaGate({ area, children, fallback, hide = false }) {
  const gate = useAreaGate(area);
  return renderGated({ ...gate, children, fallback, hide });
}
